"use client";

import { useCallback, useRef } from "react";
import { useToast } from "@/components/providers/FetchToastProvider";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useUser } from "@/hooks/useUser";
import type { DraftEvents } from "@/lib/database/events";
import { AUTOSAVE_DELAY_MS } from "../_logic/constants";
import { buildDraftUpdateFromFormData, saveDraftField } from "../_logic/draft-actions";
import type { CreateEventFormData, WizardAction } from "../_types/wizard-types";

/** A stringified snapshot of a draft update payload, one JSON string per column — compared against
 *  on the next save instead of re-stringifying the previous payload's values from scratch. */
type DraftUpdateSnapshot = Partial<Record<keyof DraftEvents, string>>;

/**
 * Returns only the keys of `next` whose value differs from `previousSnapshot` — so a debounced
 * autosave sends just the columns that actually changed instead of re-serializing and re-sending
 * the full ~20-column payload (including large jsonb blobs like `agenda`/`accommodations`) on every
 * save. `previousSnapshot` is `null` on the very first save, so the whole payload goes through
 * unchanged (the initial insert needs it). Each value in `next` is stringified once, both to build
 * this save's diff and as the snapshot to compare against next time.
 *
 * @param next - This save's full draft update, from `buildDraftUpdateFromFormData`.
 * @param previousSnapshot - The stringified snapshot from the last successful save, or `null`.
 * @returns The subset of `next` that changed, plus this save's stringified snapshot.
 */
function diffDraftUpdates(
  next: Partial<DraftEvents>,
  previousSnapshot: DraftUpdateSnapshot | null
): { diffed: Partial<DraftEvents>; snapshot: DraftUpdateSnapshot } {
  const diffed: Partial<DraftEvents> = {};
  const snapshot: DraftUpdateSnapshot = {};
  for (const key of Object.keys(next) as (keyof DraftEvents)[]) {
    const serialized = JSON.stringify(next[key]);
    snapshot[key] = serialized;
    if (!previousSnapshot || serialized !== previousSnapshot[key]) {
      (diffed as Record<string, unknown>)[key] = next[key];
    }
  }
  return { diffed, snapshot };
}

/**
 * Debounced autosave of the wizard's fields into `nowadays.draft_events`. No-ops entirely
 * for a Northstar session — Northstar leads never get a `draft_events` write during the fill (their
 * real writes happen at intake, via the service role) — and for a self-serve visitor who isn't signed
 * in yet, since `draft_events` RLS only grants authenticated writes; that visitor's progress instead
 * survives only via the guest submit path (`VenuePreviewResults`' sessionStorage round-trip through
 * login), same as Northstar. Only a signed-in, non-Northstar session persists progress here. Reuses
 * the generic `useAutoSave` debounce/re-entrancy primitive rather than hand-rolling one.
 *
 * @param formData - Current wizard form data.
 * @param dispatch - Wizard reducer dispatch, used to record the draft's id after its first save.
 * @returns The underlying `useAutoSave` state/controls (isSaving, hasUnsavedChanges, forceSave, etc.).
 */
export function useDraftAutosave(
  formData: CreateEventFormData,
  dispatch: React.Dispatch<WizardAction>
): ReturnType<typeof useAutoSave> {
  const isNorthstar = !!formData.northstarRequestId;
  const { user } = useUser();
  const { showToast } = useToast();
  const lastSavedRef = useRef<DraftUpdateSnapshot | null>(null);
  // Tracks whether the last attempt failed, so a run of consecutive failures (e.g. offline) only
  // toasts once instead of re-alerting on every 1.5s debounce tick.
  const hasWarnedRef = useRef(false);

  const onSave = useCallback(async (): Promise<boolean> => {
    if (isNorthstar || !user) return true;

    try {
      const updates = buildDraftUpdateFromFormData(formData);
      const { diffed, snapshot } = diffDraftUpdates(updates, lastSavedRef.current);

      if (Object.keys(diffed).length === 0) {
        lastSavedRef.current = snapshot;
        hasWarnedRef.current = false;
        return true;
      }

      const draftEventId = await saveDraftField(formData.draftEventId, diffed);
      lastSavedRef.current = snapshot;
      hasWarnedRef.current = false;
      if (draftEventId !== formData.draftEventId) {
        dispatch({ type: "SET_DRAFT_EVENT_ID", payload: draftEventId });
      }
      return true;
    } catch (err) {
      console.error("useDraftAutosave: save failed", err);
      // Surface it — a signed-in user filling out the wizard has no other way to know their
      // progress isn't being saved, and could lose the whole draft closing the tab silently.
      if (!hasWarnedRef.current) {
        hasWarnedRef.current = true;
        showToast(
          "Couldn't save your progress. Check your connection — your latest changes may not be saved.",
          "error"
        );
      }
      return false;
    }
  }, [formData, isNorthstar, user, dispatch, showToast]);

  return useAutoSave({
    delay: AUTOSAVE_DELAY_MS,
    onSave,
    dependencies: [formData],
  });
}
